<template>
  <div class="storage-wrapper">
    <div class="storage-header">
      <div class="header-left">
        <h3 class="title">Bins & Storage</h3>
      </div>
      <div class="header-right">
        <button class="btn pointer" @click="openAddBinModal">+ Add Bin</button>
      </div>
    </div>

    <div class="bins-container">
      <div v-if="loading" class="loader fw center">
        <loading-circle />
      </div>

      <template v-else-if="bins.length > 0">
        <div
          v-for="bin in bins"
          :key="bin.dbId"
          class="bin-card pointer"
          :class="bin.status.toLowerCase()"
          @click="openBinDetail(bin)"
        >
          <div class="bin-id elipsis-text" v-overflow-tooltip>{{ bin.id }}</div>
          <div class="bin-status">{{ bin.status }}</div>
        </div>
      </template>

      <div v-else-if="!loading" class="no-bins-message mt-4 w-100 text-center">
        <b>No Bins available for this factory.</b>
      </div>
    </div>

    <modal :open="addBinDialog" @close="closeAddBinModal()" title="Create New Storage Bin">
      <div class="add-bin-modal-content mt-2" v-if="addBinDialog">
        <div v-if="storageIdRules.length">
          <part-id-preview
            :components="storageIdRules"
            @update="updateStorageIdValues"
            title="Storage Bin ID Preview"
          />
        </div>
        <div class="input-group imp mt-2" v-if="!storageIdRules.length">
          <label class="key">Storage Bin ID:</label>
          <input type="text" v-model="manualBinId" placeholder="Enter Bin ID" />
        </div>
        <div class="flex flex-end mt-4 s">
          <button class="btn btn-black mr-2 pointer" @click="closeAddBinModal()">CANCEL</button>
          <button class="btn pointer" :disabled="isSaving" @click="handleCreateBin">CREATE</button>
        </div>
      </div>
    </modal>

    <modal :open="binDetailDialog" @close="binDetailDialog = false" title="">
      <div class="bin-modal-content">
        <div class="custom-title-bar">
          <h2 class="bin-display-id">{{ selectedBin?.id }}</h2>
          <span :class="['status-pill', selectedBin?.status?.toLowerCase()]">
            {{ selectedBin?.status }}
          </span>
        </div>

      <div class="bin-detail-body">
        <div class="inventory-section">
          <div class="section-header">
            <div class="label-group">
              <img class="section-icon" src="~@/assets/images/items-icon.svg" alt="">
              <span class="section-title">STORED INVENTORY ITEMS</span>
            </div>
          </div>

          <div class="dashed-placeholder">
            <div class="icon-circle">
            <img class="box-placeholder-icon" src="~@/assets/images/items-icon.svg" alt="">
          </div>
            <p class="placeholder-main">No items stored</p>
          </div>
        </div>

        <div class="modal-footer-actions">
          <button
            v-if="selectedBin?.status === 'AVAILABLE'"
            class="btn-status-toggle occupied w-100"
            :disabled="isUpdating"
            @click="toggleBinStatus(true)"
          >
              <span class="btn-inner">
                <span class="lock-icon"></span>
             Mark as Occupied
              </span>
          </button>
          <button
            v-else
            class="btn-status-toggle available w-100"
            :disabled="isUpdating"
            @click="toggleBinStatus(false)"
          >
            <span class="btn-inner">Mark as Available</span>
          </button>
          </div>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import modal from '@/components/common/modal.vue'
import loadingCircle from '@/components/common/loadingCircle.vue'
import partIdPreview from '@/components/common/partIdPreview.vue'
import {
  GetFactoryStorageBin,
  getTenantDefaults,
  GetIdGenerationRulesByFeatureId,
  createStorageBin,
  UpdateBinStatus
} from '@/api'
import { success, alert } from '@/plugins/notification'
import Loader from '@/plugins/loader'
import { mapGetters } from 'vuex'

export default {
  name: 'Storage',
  components: {
    modal,
    partIdPreview,
    loadingCircle
  },
  props: {
    activeFactory: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    ...mapGetters(['user'])
  },
  data () {
    return {
      bins: [],
      loading: false,
      isSaving: false,
      isUpdating: false,
      addBinDialog: false,
      binDetailDialog: false,
      selectedBin: null,
      storageIdRules: [],
      storageIdValues: [],
      manualBinId: ''
    }
  },
  watch: {
    activeFactory: {
      handler (val) {
        if (val && val.id) {
          this.fetchBins()
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    updateStorageIdValues (values) {
      this.storageIdValues = values
    },
    openBinDetail (bin) {
      this.selectedBin = bin
      this.binDetailDialog = true
    },
    async toggleBinStatus (shouldBeFull) {
      const loader = new Loader()
      loader.show()
      this.isUpdating = true
      try {
        await UpdateBinStatus(this.selectedBin.dbId, shouldBeFull)
        success(`Bin marked as ${shouldBeFull ? 'Occupied' : 'Available'}`)
        this.binDetailDialog = false
        this.fetchBins()
      } catch (err) {
        alert('Failed to update bin status')
        console.log(err)
      } finally {
        this.isUpdating = false
        loader.hide()
      }
    },
    async openAddBinModal () {
      const loader = new Loader()
      loader.show()
      try {
        const res = await getTenantDefaults()
        const storageFeatureSeq = res?.tenant_defaults?.[0]?.tenant_feature_configuration?.FACTORIES?.STORAGE_BINS?.SEQUENCE_TEMPLATE_DEFAULT
        if (storageFeatureSeq) {
          const rulesRes = await GetIdGenerationRulesByFeatureId(storageFeatureSeq)
          this.storageIdRules = rulesRes.id_generation_rules || []
          const requiresInput = this.storageIdRules.some(rule => ['Config List', 'Tags'].includes(rule.component?.caption))
          if (requiresInput) {
            this.addBinDialog = true
            loader.hide()
          } else {
            await this.executeCreateBin(this.storageIdRules, true)
            loader.hide()
          }
        } else {
          this.addBinDialog = true
          loader.hide()
        }
      } catch (err) {
        alert('Could not create bin creation')
        console.log(err)
        loader.hide()
      }
    },
    async handleCreateBin () {
      if (!this.storageIdRules.length && !this.manualBinId) return alert('Please enter a Bin ID')
      await this.executeCreateBin(this.storageIdRules, false)
    },
    async executeCreateBin (rules, skipLoader = false) {
      const loader = !skipLoader ? new Loader() : null
      if (loader) loader.show()
      this.isSaving = true
      try {
        const sequenceInput = rules.length > 0 ? this.mapSequenceInput(rules) : []
        await createStorageBin(1, sequenceInput)
        success('Storage Bin created successfully')
        this.closeAddBinModal()
        this.manualBinId = ''
        this.fetchBins()
      } catch (err) {
        alert('Unable to create bin. Please try again.')
      } finally {
        this.isSaving = false
        if (loader) loader.hide()
      }
    },
    closeAddBinModal () {
      this.addBinDialog = false
      this.storageIdValues = []
      this.manualBinId = ''
    },
    mapSequenceInput (rules) {
      return rules.map(rule => {
        const userEntry = this.storageIdValues.find(v => v.sequence === rule.sequence_order)
        const obj = {
          delimiter: rule.delimiter_after || null,
          sequence: rule.sequence_order,
          value: rule.component?.caption === 'Auto Sequence' ? null : (userEntry?.value || rule.default_value)
        }
        if (rule.component?.caption === 'User Initials') {
          obj.value = this.user.first_name?.charAt(0).toUpperCase() +
            this.user.last_name?.charAt(0).toUpperCase()
        }
        return obj
      })
    },
    fetchBins () {
      if (!this.activeFactory?.id) return
      this.loading = true
      GetFactoryStorageBin({ factoryId: this.activeFactory.id })
        .then(res => {
          if (res?.factory_storage_bins) {
            this.bins = res.factory_storage_bins.map(bin => ({
              id: bin.name,
              status: bin.is_full ? 'OCCUPIED' : 'AVAILABLE',
              dbId: bin.id
            }))
          }
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .modal-header {
  border-bottom: none !important;
  padding-bottom: 0 !important;
  margin-bottom: 0 !important;
}

::v-deep .modal-body {
  padding: 16px 18px 18px 18px !important;
}

.add-bin-modal-content {
  width: 560px;
}
.storage-wrapper {
  background: white;
  padding: 16px;
  border-radius: 8px;
  font-family: 'Inter', -apple-system, sans-serif;
}

.storage-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}

.bins-container {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.bin-id {
  max-width: 58px;
  text-overflow: ellipsis;
}

.bin-card {
  width: 90px;
  height: 60px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 6px;
  border: 1px solid;
  font-size: 0.7rem;
  font-weight: 600;

  &.occupied { background: #fff1f1; border-color: #ffdadb; color: #d63031; }
  &.available { background: #f1fff6; border-color: #d1f5e0; color: #27ae60; }

  .bin-status { font-size: 0.55rem; text-transform: uppercase; }
}
.bin-modal-content {
  min-width: 400px;
}

.custom-title-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 16px;
  margin-top: -6px;

  .bin-display-id {
    font-size: var(--text-s);
    font-weight: 600;
    color: #111827;
    margin: 0;
  }
}

.status-pill {
  font-size: 8.5px;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 700;
  text-transform: uppercase;

  &.occupied { background: #fee2e2; color: #dc2626; }
  &.available { background: #dcfce7; color: #16a34a; }
}

.inventory-section {
  margin-bottom: 18px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;

  .label-group {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #6b7280;
    .section-icon { width: 14px; height: 14px; }
    .section-title { font-size: var(--text-xs); font-weight: 500; }
  }

  .assign-link {
    background: none; border: none; color: #4f46e5;
    font-weight: 600; font-size: var(--text-xs2); cursor: pointer;
  }
}

.dashed-placeholder {
  border: 1px dashed #e5e7eb;
  border-radius: 6px;
  padding: 24px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #fafafa;

  .icon-circle {
    color: #9ca3af; margin-bottom: 6px;
    .box-placeholder-icon { width: 32px; height: 32px; }
  }
  .placeholder-main { font-weight: 500; color: #6b7280; font-size: var(--text-xs2); margin: 0; }
  .placeholder-sub { font-size: var(--text-xs1); color: #9ca3af; margin-top: 2px; }
}

.modal-footer-actions {
  border-top: 1px solid #f3f4f6;
  padding-top: 14px;
}

.btn-status-toggle {
  border: none;
  border-radius: 3px;
  padding: 10px;
  font-weight: 600;
  font-size: 12.5px;
  cursor: pointer;

  &.occupied { background: #e61919; color: white; }
  &.available { background: #10b981; color: white; }

  .btn-inner {
    display: flex; justify-content: center; align-items: center; gap: 6px;
    .lock-icon { font-size: var(--text-xs2); }
  }
}

.w-100 { width: 100%; }
.loader { height: 200px; display: flex; align-items: center; justify-content: center; }
</style>
